import { FilterState } from 'components/FilterDrawer/types';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const COLUMN_FILTER_STATE_KEY = 'pipelineRunStatus_column_filter_state';

const createFilterCache = (namespace = '') => {
  const keyFor = (key: string) => namespace ? `${namespace}_${key}` : key;

  return {
  // Get cached data for a specific field and filter state
  get<T>(field: string, filterStateKey: string): T | null {
    try {
      const cacheKey = keyFor(`filter_${field}_${filterStateKey}`);
      const cached = sessionStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const item: CacheItem<T> = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - item.timestamp > item.ttl) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }
      
      return item.data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  },

  // Set data in cache
  set<T>(field: string, filterStateKey: string, data: T): void {
    try {
      const cacheKey = keyFor(`filter_${field}_${filterStateKey}`);
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        ttl: CACHE_TTL,
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(item));
    } catch (error) {
      console.error('Error writing to cache:', error);
    }
  },

  // Clear cache for a specific field
  clear(field: string): void {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(keyFor(`filter_${field}_`))) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  },

  // Clear all filter caches
  clearAll(): void {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(keyFor('filter_'))) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing all caches:', error);
    }
  },

  // Generate a unique key based on current filter state
  generateStateKey(filters: FilterState, excludedFields: string[] = []): string {
    const filterCopy = { ...filters };
    excludedFields.forEach(field => delete filterCopy[field as keyof FilterState]);
    
    const sortedKeys = Object.keys(filterCopy).sort();
    const stateString = sortedKeys
      .map(key => {
        const value = filterCopy[key as keyof FilterState];
        if (Array.isArray(value)) {
          const sortedValues = [...value].sort((a, b) => {
            const valA = String(a?.value ?? a ?? '');
            const valB = String(b?.value ?? b ?? '');
            return valA.localeCompare(valB);
          });
          if (sortedValues.length === 0) return `${key}:[]`;
          return `${key}:${JSON.stringify(sortedValues.map(v => v?.value ?? v))}`;
        }
        return `${key}:${JSON.stringify(value)}`;
      })
      .join('|');
    
    return stateString;
  },

  // Generate a unique key based on only relevant fields
  generateStateKeyWithRelevantFields(filters: FilterState, relevantFields: string[]): string {
    const filteredCopy: any = {};
    
    if (relevantFields.length === 0) {
      return 'no_dependencies';
    }
    
    relevantFields.forEach(field => {
      if (filters[field as keyof FilterState] !== undefined) {
        filteredCopy[field] = filters[field as keyof FilterState];
      }
    });
    
    const sortedKeys = Object.keys(filteredCopy).sort();
    const stateString = sortedKeys
      .map(key => {
        const value = filteredCopy[key];
        if (Array.isArray(value)) {
          const sortedValues = [...value].sort((a, b) => {
            const valA = String(a?.value ?? a ?? '');
            const valB = String(b?.value ?? b ?? '');
            return valA.localeCompare(valB);
          });
          if (sortedValues.length === 0) return `${key}:[]`;
          return `${key}:${JSON.stringify(sortedValues.map(v => v?.value ?? v))}`;
        }
        return `${key}:${JSON.stringify(value)}`;
      })
      .join('|');
    
    return stateString || 'no_state';
  },

  // Save applied filters to sessionStorage
  saveAppliedFilters(filters: FilterState): void {
    try {
      sessionStorage.setItem(keyFor('applied_filters'), JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving applied filters:', error);
    }
  },

  // Get applied filters from sessionStorage
  getAppliedFilters(): FilterState | null {
    try {
      const saved = sessionStorage.getItem(keyFor('applied_filters'));
      if (!saved) return null;
      return JSON.parse(saved);
    } catch (error) {
      console.error('Error getting applied filters:', error);
      return null;
    }
  },

  // Clear applied filters
  clearAppliedFilters(): void {
    try {
      sessionStorage.removeItem(keyFor('applied_filters'));
    } catch (error) {
      console.error('Error clearing applied filters:', error);
    }
  },

  saveColumnFilterState(state: any): void {
    try {
      sessionStorage.setItem(keyFor(COLUMN_FILTER_STATE_KEY), JSON.stringify(state));
    } catch (error) {
      console.error('Error saving column filter state:', error);
    }
  },

  getColumnFilterState<T>(): T | null {
    try {
      const saved = sessionStorage.getItem(keyFor(COLUMN_FILTER_STATE_KEY));
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error getting column filter state:', error);
      return null;
    }
  },

  clearColumnFilterState(): void {
    try {
      sessionStorage.removeItem(keyFor(COLUMN_FILTER_STATE_KEY));
    } catch (error) {
      console.error('Error clearing column filter state:', error);
    }
  }
  };
};

export const filterCache = createFilterCache();
export const createNamespacedFilterCache = (namespace: string) => createFilterCache(namespace);